"use client";

import { useState } from "react";
import {
  CipherGrid,
  type CipherMode,
  CipherWorkbench,
} from "@/components/tool-kit/cipher-workbench";
import {
  ErrorNotice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT } from "@/components/tool-kit/styles";
import { SubstitutionSolver } from "@/components/tools/cipher-machines/substitution-solver";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  ALPHABET,
  applyTextOptions,
  KEEP_ALL_TEXT,
} from "@/lib/tool-kit/ciphers/alphabet";
import {
  keywordKey,
  randomKey,
  readKey,
  substitutionDecode,
  substitutionEncode,
} from "@/lib/tool-kit/ciphers/substitution";

type Task = "solve" | "cipher";
type KeySource = "keyword" | "random" | "typed";

/** English strings. A translated content file overrides these through `ui`. */
export const SUBSTITUTION_CIPHER_STRINGS = {
  task: "What do you want to do?",
  taskSolve: "Solve a cryptogram",
  taskCipher: "Encode or decode with a key",
  cryptogram: "Cipher text to solve",
  cryptogramSample:
    "LMLHQ FYGTOYTL DYR DYNKPR, YGJ Y RKXAFL RONRPKPOPKUG WKADLH SLLAR YFF UE PDLX. PDL WUXXUGLRP FLPPLHR RPYQ PDL WUXXUGLRP, PDL RDUHP ZUHJR RPYQ RDUHP, YGJ Y JUONFLJ FLPPLH KR RPKFF JUONFLJ. TOLRR PDL ZUHJ PDYP YAALYHR XURP UEPLG YGJ PDL HLRP UE PDL XLRRYTL RPYHPR PU EYFF KGPU AFYWL.",
  cryptogramPlaceholder: "Paste a cryptogram — keep its spaces if it has them",
  noLetters: "Paste some cipher text to see its letter frequencies.",
  frequencyHeading: "Letter frequencies",
  frequencyHint:
    "Top row: your cipher letters, commonest first (blue bars). Bottom row: English letters in their usual order (amber bars). Letters in the same column are a first guess, not an answer — on a short text the top few often swap places.",
  letterTotal:
    "one: {count} letter in the cipher text|other: {count} letters in the cipher text",
  showTable: "Show the counts as a table",
  rankColumn: "Rank",
  cipherColumn: "Cipher letter",
  countColumn: "Count",
  shareColumn: "Share",
  englishColumn: "English letter",
  englishShareColumn: "English share",
  guessesHeading: "Your guesses",
  guessesHint:
    "Type the plain letter you think each cipher letter stands for. Letters that do not occur in the cipher text are dimmed.",
  guessFor: "Plain letter for cipher letter {letter}",
  guessed: "Guessed {solved} of {total}",
  conflict:
    "one: Plain letter {letters} is guessed for two cipher letters — only one of them can be right.|other: Plain letters {letters} are each guessed for two cipher letters — only one of each pair can be right.",
  fillFromFrequency: "Start from frequency order",
  clearGuesses: "Clear all guesses",
  hintsHeading: "Short-word hints",
  hintsHint:
    "The commonest English words that fit each short cipher word — same letter pattern, and consistent with your guesses. Select one to try it.",
  noShortWords:
    "No one-, two- or three-letter words. Without spaces, work from the letter frequencies and look for common pairs such as TH, HE and IN.",
  wordCount: "one: once|other: {count} times",
  noCandidates: "Nothing common fits — check your guesses.",
  tryWord: "Try {word} for {cipher}",
  doublesHeading: "Doubled letters",
  doublesHint:
    "A doubled cipher pair is a doubled plain pair. In English the commonest are LL, EE, OO, SS and TT.",
  doubleCount: "{pair} × {count}",
  noDoubles: "No doubled letters inside the cipher words.",
  solutionHeading: "Partial decryption",
  solutionHint:
    "Guessed letters are shown in lower case under their cipher letters; a dot marks a letter you have not guessed yet. The copy puts unknown letters in capitals.",
  viewLimit: "Showing the first {count} characters.",
  copySolution: "Copy partial decryption",
  copied: "Copied!",
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Plain text",
  cipherText: "Cipher text",
  decodedText: "Decoded text",
  inputPlaceholder: "Type or paste your message",
  sample: "Flee at once. We are discovered!",
  keySource: "Key",
  keyKeyword: "From a keyword",
  keyRandom: "Random",
  keyTyped: "Type a full key",
  keyword: "Keyword",
  keywordSample: "ZEBRAS",
  keywordHint:
    "The keyword's letters come first, each once, then the rest of the alphabet in order.",
  newRandomKey: "New random key",
  randomHint:
    "Shuffled with your browser's secure random generator. No letter stands for itself.",
  randomPrompt: "Press New random key to make one.",
  typedKey: "Cipher alphabet — the 26 letters that A to Z become",
  keyMissing: "Missing: {letters}",
  keyRepeated: "Used more than once: {letters}",
  keyTable: "Key: plain letters and the cipher letters they become",
  plainRow: "Plain",
  cipherRow: "Cipher",
  keep: "Keep",
  keepCase: "Upper and lower case",
  keepSpaces: "Spaces and line breaks",
  keepPunctuation: "Punctuation and numbers",
  copy: "Copy result",
  clear: "Clear",
  swap: "Swap input and output",
  working: "The key",
} as const;

export type SubstitutionStrings = typeof SUBSTITUTION_CIPHER_STRINGS;

function KeyControls({
  source,
  onSource,
  keyword,
  onKeyword,
  random,
  onRandom,
  typed,
  onTyped,
  strings,
}: {
  source: KeySource;
  onSource: (next: KeySource) => void;
  keyword: string;
  onKeyword: (next: string) => void;
  random: string | null;
  onRandom: () => void;
  typed: string;
  onTyped: (next: string) => void;
  strings: SubstitutionStrings;
}) {
  const reading = readKey(typed);
  return (
    <div className="space-y-4">
      <PillGroup
        label={strings.keySource}
        name="sub-key-source"
        onChange={onSource}
        options={[
          { value: "keyword", label: strings.keyKeyword },
          { value: "random", label: strings.keyRandom },
          { value: "typed", label: strings.keyTyped },
        ]}
        value={source}
      />
      {source === "keyword" ? (
        <TextField
          hint={strings.keywordHint}
          id="sub-keyword"
          label={strings.keyword}
          mono
          onChange={onKeyword}
          value={keyword}
        />
      ) : null}
      {source === "random" ? (
        <div className="space-y-2">
          <button className={BUTTON_SECONDARY} onClick={onRandom} type="button">
            {strings.newRandomKey}
          </button>
          <p className={HINT}>
            {random ? strings.randomHint : strings.randomPrompt}
          </p>
        </div>
      ) : null}
      {source === "typed" ? (
        <div className="space-y-2">
          <TextField
            id="sub-typed-key"
            label={strings.typedKey}
            mono
            onChange={onTyped}
            value={typed}
          />
          {reading.key === null && reading.missing.length > 0 ? (
            <p className={HINT}>
              {t(strings.keyMissing, { letters: reading.missing.join(" ") })}
            </p>
          ) : null}
          {reading.repeated.length > 0 ? (
            <ErrorNotice>
              {t(strings.keyRepeated, { letters: reading.repeated.join(" ") })}
            </ErrorNotice>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function CipherPanel({ strings }: { strings: SubstitutionStrings }) {
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [options, setOptions] = useState(KEEP_ALL_TEXT);
  const [source, setSource] = useState<KeySource>("keyword");
  const [keyword, setKeyword] = useState<string>(strings.keywordSample);
  const [random, setRandom] = useState<string | null>(null);
  const [typed, setTyped] = useState("");
  const keys: Record<KeySource, string | null> = {
    keyword: keywordKey(keyword),
    random,
    typed: readKey(typed).key,
  };
  const key = keys[source];
  let output = "";
  if (key) {
    const raw =
      mode === "encode"
        ? substitutionEncode(input, key)
        : substitutionDecode(input, key);
    output = applyTextOptions(raw, options);
  }
  return (
    <CipherWorkbench
      idPrefix="sub"
      input={input}
      keyControls={
        <KeyControls
          keyword={keyword}
          onKeyword={setKeyword}
          onRandom={() => setRandom(randomKey())}
          onSource={setSource}
          onTyped={setTyped}
          random={random}
          source={source}
          strings={strings}
          typed={typed}
        />
      }
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: mode === "encode" ? strings.plainText : strings.cipherText,
        inputPlaceholder: strings.inputPlaceholder,
        output: mode === "encode" ? strings.cipherText : strings.decodedText,
        textOptions: strings.keep,
        keepCase: strings.keepCase,
        keepSpaces: strings.keepSpaces,
        keepPunctuation: strings.keepPunctuation,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      onInputChange={setInput}
      onModeChange={setMode}
      output={output}
      textOptions={{
        value: options,
        onChange: setOptions,
        show: ["keepCase", "keepSpaces", "keepPunctuation"],
      }}
      working={
        key ? (
          <CipherGrid
            cells={[...ALPHABET, ...key]}
            columns={ALPHABET.length}
            dense
            label={strings.keyTable}
            rowHeaders={[strings.plainRow, strings.cipherRow]}
          />
        ) : null
      }
    />
  );
}

export default function SubstitutionCipherWidget() {
  const strings = useToolUi(SUBSTITUTION_CIPHER_STRINGS);
  const [task, setTask] = useState<Task>("solve");
  return (
    <>
      <PillGroup
        label={strings.task}
        name="sub-task"
        onChange={setTask}
        options={[
          { value: "solve", label: strings.taskSolve },
          { value: "cipher", label: strings.taskCipher },
        ]}
        value={task}
      />
      {task === "solve" ? (
        <SubstitutionSolver strings={strings} />
      ) : (
        <CipherPanel strings={strings} />
      )}
    </>
  );
}
